import { Fragment, useEffect, useState, useCallback } from "react";
import { createConnectedAccount, getStripeConnectedAccount } from "../../services/payments/onboardConnectedAccount";
import useStripeConnect from "../../services/payments/useStripeConnect";
import DisplayConnectAccountOnboarding from "../onboardCpa/connectedAccount/DisplayConnectAccountOnboarding";
import StatusConnectedAccount from "../onboardCpa/connectedAccount/StatusConnectedAccount";
import LoadingPlaceholder from "../ui/loading/LoadingPlaceholder";
import { useAtom } from "jotai/react";
import { getUpdateOnboardProcess } from "../onboardCpa/connectedAccount/storeStates";

function ConnectedAccountFlow({ 
  onComplete,
  title = "Get paid, securely and on time.",
  description = "To start accepting payments, connect your Stripe account."
}) {
  const [connectedAccountId, setConnectedAccountId] = useState();
  const [hasConnectedAccount, setHasConnectedAccount] = useState(false);
  const [connectedAccountOnboardFinished, setConnectedAccountOnboardFinished] = useAtom(getUpdateOnboardProcess);
  const [connectedAccountPending, setConnectedAccountPending] = useState(false);
  const [connectedAccountChargesEnabled, setConnectedAccountChargesEnabled] = useState(false);
  const [connectedAccountPayoutsEnabled, setConnectedAccountPayoutsEnabled] = useState(false);
  const [connectedAccountRejected, setConnectedAccountRejected] = useState(false);
  const [accountCreatePending, setAccountCreatePending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [hasCompletedCallback, setHasCompletedCallback] = useState(false);
  
  const stripeConnectInstance = useStripeConnect(connectedAccountId);

  // Fetch existing connected account status
  const getConnectAccount = useCallback(async () => {
    setLoading(true);
    const url = "payments/create-connected-account";

    try {
      const response = await getStripeConnectedAccount(url);
      if (!response.ok) {
        throw new Error(response.message);
      }
      
      const data = await response.json();
      setConnectedAccountId(data.account);
      setHasConnectedAccount(data.hasConnectedAccount);
      setConnectedAccountOnboardFinished(data.account_info?.detailsSubmitted);
      setConnectedAccountPending(data.account_info?.pendingVerification);
      setConnectedAccountChargesEnabled(data.account_info?.chargesEnabled);
      setConnectedAccountPayoutsEnabled(data.account_info?.payoutsEnabled);
      setConnectedAccountRejected(data.account_info?.rejected);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching connected account:", err);
      setLoading(false);
    }
  }, [setConnectedAccountOnboardFinished]);

  useEffect(() => {
    getConnectAccount();
  }, [connectedAccountOnboardFinished, getConnectAccount]);

  // Create connected account
  async function connectAccount() {
    setAccountCreatePending(true);
    const url = "payments/create-connected-account";

    createConnectedAccount(url)
      .then((response) => response.json())
      .then((json) => {
        setAccountCreatePending(false);
        const { account, error } = json;

        if (account) {
          setConnectedAccountId(account);
        }

        if (error) {
          setError(true);
        }
      });
  }

  // Check if account is fully set up and trigger onComplete callback
  // Only fire once when payouts become enabled (not if already enabled on load)
  useEffect(() => {
    if (connectedAccountPayoutsEnabled && onComplete && !hasCompletedCallback && connectedAccountOnboardFinished) {
      // Only trigger if onboarding was just completed (not pre-existing account)
      setHasCompletedCallback(true);
      onComplete({
        accountId: connectedAccountId,
        chargesEnabled: connectedAccountChargesEnabled,
        payoutsEnabled: connectedAccountPayoutsEnabled
      });
    }
  }, [connectedAccountPayoutsEnabled, connectedAccountChargesEnabled, connectedAccountId, connectedAccountOnboardFinished, onComplete, hasCompletedCallback]);

  // One-time page reload (required by Stripe Connect)
  useEffect(() => {
    const hasReloaded = localStorage.getItem("hasReloaded");
    if (!hasReloaded) {
      localStorage.setItem("hasReloaded", "true");
      window.location.replace(window.location.href);
    }
  }, []);

  if (loading) {
    return (
      <div className="internal-content-body">
        <LoadingPlaceholder rows={1} type="card-large" />
      </div>
    );
  }

  return (
    <Fragment>
      {!hasConnectedAccount && !stripeConnectInstance && (
        <div id="terms">
          <h1>{title}</h1>
          <p>{description}</p>
          <ul>
            <li><strong>Secure:</strong> Stripe keeps every transaction compliant</li>
            <li><strong>Fast:</strong> Direct deposits straight to your bank</li>
            <li><strong>Reliable:</strong> Never miss a payout</li>
          </ul>
          <p>It takes two minutes, and you're ready to earn.</p>
          {!accountCreatePending && !connectedAccountId && (
            <div>
              <button
                style={{ margin: "0 auto", marginTop: "40px" }}
                className="cta-button primary"
                onClick={connectAccount}
              >
                <b>Connect with Stripe</b>
              </button>
            </div>
          )}
        </div>
      )}
      
      {!hasConnectedAccount && stripeConnectInstance && (
        <DisplayConnectAccountOnboarding
          stripeConnectInstance={stripeConnectInstance}
        />
      )}
      
      {hasConnectedAccount && (
        <StatusConnectedAccount
          pendingVerification={connectedAccountPending}
          chargesEnabled={connectedAccountChargesEnabled}
          payoutsEnabled={connectedAccountPayoutsEnabled}
          accountRejected={connectedAccountRejected}
          connectedAccountId={connectedAccountId}
        />
      )}
    </Fragment>
  );
}

export default ConnectedAccountFlow;